import { normalizeMenuPath } from "./layoutTheme";

export type EnquiryLane = "" | "add_query" | "list" | "cancel_list";
export type BookingLane = "list" | "add";

function normalizeMenuKey(value?: string): string {
    return (value || "").toLowerCase().replace(/[\s/_-]+/g, "");
}

function menuKeys(item: { moduleCode?: string; code?: string; moduleName?: string }): string[] {
    return [item.moduleCode, item.code, item.moduleName].map(normalizeMenuKey).filter(Boolean);
}

export function enquiryAddPath(): string {
    return "/add-enquiry";
}

export function enquiryListPath(): string {
    return "/enquiry-list";
}

export function enquiryCancelListPath(): string {
    return "/cancel-enquiry-list";
}

export function isAddEnquiryMenuItem(item: {
    moduleCode?: string;
    code?: string;
    moduleName?: string;
}): boolean {
    return menuKeys(item).some(
        (key) => key === "addenquiry" || key === "tmsaddenquiry" || key.endsWith("addenquiry")
    );
}

const MENU_KEY_TO_PATH: Record<string, string> = {
    addenquiry: enquiryAddPath(),
    tmsaddenquiry: enquiryAddPath(),
    enquirylist: enquiryListPath(),
    tmsenquirylist: enquiryListPath(),
    cancelenquirylist: enquiryCancelListPath(),
    tmscancelenquirylist: enquiryCancelListPath(),
    enquirynow: enquiryListPath(),
    bookinglist: bookingListPath(),
    tmsbookinglist: bookingListPath(),
    addbooking: bookingAddPath(),
    tmsaddbooking: bookingAddPath(),
    proforma: bookingListPath(),
};

function normalizeNavPath(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("/") || trimmed.startsWith("?")) return trimmed;
    return `/${trimmed}`;
}

function resolveLegacyEnquiryUrl(raw: string): string | undefined {
    const [pathPart, queryPart] = raw.split("?");
    const pathLower = normalizeMenuPath(pathPart).toLowerCase();

    if (queryPart) {
        const params = new URLSearchParams(queryPart);
        if (params.get("lane") === "add_query") return enquiryAddPath();
        if (params.get("lane") === "cancel_list") return enquiryCancelListPath();
        if (params.get("lane") === "list") return enquiryListPath();
    }

    if (pathLower === "/enquiry" || pathLower === "/enquirynow") {
        return enquiryListPath();
    }

    return undefined;
}

/** Resolves the navigation URL for a TMS menu item (API url, code, or legacy lane paths). */
export function resolveMenuNavUrl(item: {
    url?: string;
    moduleCode?: string;
    code?: string;
    moduleName?: string;
}): string | undefined {
    if (isAddEnquiryMenuItem(item)) {
        return enquiryAddPath();
    }

    const rawUrl = item?.url?.trim();
    if (rawUrl) {
        const legacy = resolveLegacyEnquiryUrl(rawUrl);
        if (legacy) return legacy;
        return normalizeNavPath(rawUrl);
    }

    for (const key of menuKeys(item)) {
        if (MENU_KEY_TO_PATH[key]) return MENU_KEY_TO_PATH[key];
    }

    const slug = (item.code || item.moduleCode || "").trim().toLowerCase();
    if (slug && /^[a-z0-9-]+$/.test(slug)) {
        return `/${slug}`;
    }

    return undefined;
}

export function resolveEnquiryLane(pathname: string, search = ""): EnquiryLane {
    const path = normalizeMenuPath(pathname).toLowerCase();

    if (path === enquiryAddPath() || path === "/addenquiry") return "add_query";
    if (path === enquiryCancelListPath() || path === "/cancelenquirylist") return "cancel_list";
    if (
        path === enquiryListPath() ||
        path === "/enquirylist" ||
        path === "/enquiry" ||
        path === "/enquirynow"
    ) {
        const query = search.startsWith("?") ? search.slice(1) : search;
        const lane = new URLSearchParams(query).get("lane");
        if (lane === "add_query") return "add_query";
        if (lane === "cancel_list") return "cancel_list";
        return "list";
    }

    return "";
}

export function resolveBookingLane(pathname: string, search = ""): BookingLane {
    const path = normalizeMenuPath(pathname).toLowerCase();
    if (path === "/addbooking") return "add";

    const query = search.startsWith("?") ? search.slice(1) : search;
    const lane = new URLSearchParams(query).get("lane");
    if (lane === "add") return "add";

    return "list";
}

export function bookingListPath(): string {
    return "/bookinglist";
}

export function bookingAddPath(): string {
    return "/addbooking";
}
